import { useState, useEffect } from 'react';
import { notamApi } from '../lib/api';
import { 
  AlertTriangle, 
  Info, 
  AlertCircle, 
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Plane
} from 'lucide-react';

const NotamPanel = ({ lat, lng, icao }) => {
  const [notams, setNotams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [source, setSource] = useState(null);

  const loadNotams = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      if (icao) {
        response = await notamApi.getByIcao(icao);
      } else if (lat && lng) {
        // 100 km default — covers the typical NOTAM zone (50-80 NM)
        // and triggers the backend FIR fallback for rural missions
        // when no airport is within range.
        response = await notamApi.getByLocation(lat, lng, 100000);
      } else {
        setError('Position non disponible');
        return;
      }
      
      setNotams(response.data.notams || []);
      setSource(response.data.source);
    } catch (err) {
      console.error('Failed to load NOTAMs:', err);
      setError('Impossible de charger les NOTAMs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (lat && lng) {
      loadNotams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, icao]);

  const getImportanceConfig = (importance) => {
    switch (importance) {
      case 'high':
        return { 
          icon: AlertTriangle, 
          color: 'text-red-500', 
          bg: 'bg-red-50',
          border: 'border-red-200',
          label: 'Important'
        };
      case 'medium':
        return { 
          icon: AlertCircle, 
          color: 'text-amber-500', 
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          label: 'Attention'
        };
      default:
        return { 
          icon: Info, 
          color: 'text-blue-500', 
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          label: 'Information'
        };
    }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      // AutoRouter / EAD timestamps may arrive as either ISO 8601 or as
      // a 10-digit Unix epoch number-string ("1778479200"). Detect the
      // numeric case defensively so the panel never renders "1970"
      // dates if a future backend regression slips through.
      let value = dateString;
      if (typeof value === 'string' && /^\d{10}$/.test(value)) {
        value = Number(value) * 1000;
      } else if (typeof value === 'number' && value < 1e12) {
        value = value * 1000;
      }
      return new Date(value).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Plane className="w-5 h-5 text-sky-500" />
          <h3 className="font-semibold text-slate-900 font-[Barlow]">
            NOTAMs
          </h3>
          {notams.length > 0 && (
            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
              {notams.length}
            </span>
          )}
        </div>
        <button
          onClick={loadNotams}
          disabled={loading}
          className="p-2 text-slate-400 hover:text-sky-500 hover:bg-sky-50 rounded transition-colors"
          title="Actualiser"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="p-4">
        {loading && notams.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        )}

        {error && (
          <div className="text-center py-6 text-red-500">
            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && notams.length === 0 && (
          <div className="text-center py-6 text-slate-500">
            <Info className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">Aucun NOTAM actif dans cette zone</p>
          </div>
        )}

        {notams.length > 0 && (
          <div className="space-y-3">
            {notams.map((notam) => {
              const config = getImportanceConfig(notam.importance);
              const Icon = config.icon;
              const isExpanded = expanded[notam.id];

              return (
                <div 
                  key={notam.id}
                  className={`rounded-lg border ${config.border} ${config.bg} overflow-hidden`}
                >
                  <button
                    onClick={() => toggleExpand(notam.id)}
                    className="w-full p-3 flex items-start gap-3 text-left"
                  >
                    <Icon className={`w-5 h-5 ${config.color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs font-medium text-slate-700">
                          {notam.id}
                        </span>
                        <span className={`px-1.5 py-0.5 text-xs rounded ${config.color} ${config.bg}`}>
                          {notam.type}
                        </span>
                        {notam.icao && (
                          <span className="px-1.5 py-0.5 text-xs bg-slate-200 text-slate-600 rounded">
                            {notam.icao}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-700 line-clamp-2">
                        {notam.message}
                      </p>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 pt-0 border-t border-slate-200/50">
                      <div className="mt-3 space-y-2 text-xs text-slate-600">
                        <div className="flex justify-between">
                          <span>Début validité:</span>
                          <span className="font-medium">{formatDate(notam.start_validity)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Fin validité:</span>
                          <span className="font-medium">{formatDate(notam.end_validity)}</span>
                        </div>
                        {notam.airport_name && (
                          <div className="flex justify-between">
                            <span>Aéroport:</span>
                            <span className="font-medium">{notam.airport_name}</span>
                          </div>
                        )}
                      </div>
                      
                      {notam.raw && (
                        <div className="mt-3 p-2 bg-slate-100 rounded text-xs font-mono text-slate-600 whitespace-pre-wrap overflow-x-auto">
                          {notam.raw}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-700">
              <p className="font-medium mb-1">Avertissement</p>
              <p>
                Ces NOTAMs sont fournis à titre indicatif. Consultez toujours les sources officielles 
                (<a href="https://sofia-briefing.aviation-civile.gouv.fr" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline inline-flex items-center gap-0.5">
                  SIA/OLIVIA <ExternalLink className="w-3 h-3" />
                </a>) avant tout vol.
              </p>
            </div>
          </div>
        </div>

        {source && (
          <p className="mt-2 text-xs text-slate-400 text-right">
            Source: {source}
          </p>
        )}
      </div>
    </div>
  );
};

export default NotamPanel;
